import React, { useCallback, useMemo, useState } from "react";
import { Tag } from "antd";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useDispatch, useSelector } from "react-redux";

import BoardColumn from "./BoardColumn";
import { moveIssue } from "../../../../core/data/redux/projects/thunks";
import { selectActiveSprint } from "../../../../core/data/redux/projects/selectors";
import { ISSUE_TYPES, PRIORITY_OPTIONS } from "../../constants/projects.constants";

import type {
  Issue,
  Status,
} from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

interface BoardProps {
  projectKey: string;
  statuses: Status[];
  issues: Issue[];
  onCreateIssueClick: (statusId: number) => void;
}

const Board: React.FC<BoardProps> = ({
  projectKey,
  statuses,
  issues,
  onCreateIssueClick,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const activeSprint = useSelector(selectActiveSprint);
  const [optimistic, setOptimistic] = useState<{
    issues: Issue[];
  } | null>(null);
  const [activeIssue, setActiveIssue] = useState<Issue | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const collisionDetection: CollisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return rectIntersection(args);
  }, []);

  const sortedStatuses = useMemo(
    () => [...statuses].sort((a, b) => a.position - b.position),
    [statuses]
  );

  const issuesToShow = optimistic?.issues || issues;

  const issuesByColumn = useMemo(() => {
    const map = new Map<number, Issue[]>();
    sortedStatuses.forEach((s) => map.set(Number(s.id), []));
    for (const issue of issuesToShow) {
      const list = map.get(Number(issue.status_id));
      if (list) list.push(issue);
    }
    sortedStatuses.forEach((s) => {
      const list = map.get(Number(s.id)) || [];
      list.sort((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0));
      map.set(Number(s.id), list);
    });
    return map;
  }, [issuesToShow, sortedStatuses]);

  const findIssueByDragId = (dragId: string): Issue | undefined => {
    const numId = Number(dragId.replace("issue-", ""));
    return issuesToShow.find((i) => Number(i.id) === numId);
  };

  const findColumnByDragId = (
    dragId: string
  ): { statusId: number; before: Issue | null; after: Issue | null } | null => {
    if (dragId.startsWith("column-")) {
      const statusId = Number(dragId.replace("column-", ""));
      const list = issuesByColumn.get(statusId) || [];
      return { statusId, before: list[list.length - 1] || null, after: null };
    }
    if (dragId.startsWith("issue-")) {
      const target = findIssueByDragId(dragId);
      if (!target) return null;
      const list = issuesByColumn.get(Number(target.status_id)) || [];
      const idx = list.findIndex((i) => Number(i.id) === Number(target.id));
      const before = idx > 0 ? list[idx - 1] : null;
      const after = list[idx] || null;
      return { statusId: Number(target.status_id), before, after };
    }
    return null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    if (id.startsWith("issue-")) {
      const found = findIssueByDragId(id);
      if (found) setActiveIssue(found);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveIssue(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const dragged = findIssueByDragId(String(active.id));
    if (!dragged) return;

    const target = findColumnByDragId(String(over.id));
    if (!target) return;

    const newStatusId = target.statusId;
    const beforeId =
      target.before && target.before.id !== dragged.id ? target.before.id : null;
    const afterId =
      target.after && target.after.id !== dragged.id ? target.after.id : null;

    const optimisticIssues = issuesToShow.map((i) =>
      Number(i.id) === Number(dragged.id) ? { ...i, status_id: newStatusId } : i
    );
    setOptimistic({ issues: optimisticIssues });

    try {
      await dispatch(
        moveIssue({
          id: Number(dragged.id),
          status_id: newStatusId,
          sprint_id: activeSprint ? activeSprint.id : null,
          before_id: beforeId ? Number(beforeId) : null,
          after_id: afterId ? Number(afterId) : null,
        })
      ).unwrap();
    } catch {
      // revert on error
    } finally {
      setOptimistic(null);
    }
  };

  const overlayType = activeIssue
    ? ISSUE_TYPES.find((t) => t.value === activeIssue.type)
    : null;
  const overlayPriority = activeIssue
    ? PRIORITY_OPTIONS.find((p) => p.value === activeIssue.priority)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveIssue(null)}
    >

      <div
        className="d-flex gap-3 bg-white rounded-3 p-3"
        style={{ overflowX: "auto", overflowY: "visible", padding: "4px 4px 16px" }}
      >
        {sortedStatuses.map((status) => (
          <BoardColumn
            key={status.id}
            status={status}
            issues={issuesByColumn.get(Number(status.id)) || []}
            projectKey={projectKey}
            onCreateIssueClick={onCreateIssueClick}
          />
        ))}
      </div>

      <DragOverlay dropAnimation={{ duration: 180 }}>
        {activeIssue && (
          <div
            className="card border-0 overflow-hidden"
            style={{
              borderRadius: 8,
              minWidth: 260,
              maxWidth: 320,
              boxShadow: "0 18px 36px rgba(59,130,246,0.35)",
              background: "#fff",
              cursor: "grabbing",
            }}
          >
            <div style={{ height: 3, background: overlayType?.color || "#6B7280" }} />
            <div style={{ padding: "10px 12px 8px" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>
                {activeIssue.title}
              </div>
              <div className="d-flex align-items-center gap-2" style={{ fontSize: 11, color: "#64748b" }}>
                {overlayType && (
                  <span style={{ color: overlayType.color }}>
                    <span className="d-inline-block rounded-circle me-1" style={{ width: 6, height: 6, background: overlayType.color }} />
                    {overlayType.label}
                  </span>
                )}
                <span className="ms-auto fw-semibold text-primary">{activeIssue.issue_key}</span>
              </div>
              {overlayPriority && (
                <div className="mt-2">
                  <Tag color="default" style={{ margin: 0, fontSize: 10, color: overlayPriority.color, fontWeight: 600 }}>
                    {overlayPriority.label}
                  </Tag>
                </div>
              )}
            </div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default Board;
